import { apiClient } from './apiClient';
import { getAppUrlInfo } from '../utils/urls';

export enum ChatModule {
  EXPENSES = 'expenses',
  USERS = 'users',
  GENERAL = 'general',
}

export interface ChatRequest {
  prompt: string;
  module?: ChatModule;
  dateRange?: string[];
  categories?: string[];
}

export interface ChatResponse {
  response: string;
  data?: any;
  metadata?: {
    queriesExecuted: number;
    dataPoints: number;
    module: string;
  };
}

export interface StreamingChatResponse {
  type: 'chunk' | 'complete' | 'error';
  content?: string;
  data?: any;
  metadata?: {
    queriesExecuted: number;
    dataPoints: number;
    module: string;
  };
  error?: string;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  data?: any;
  metadata?: any;
  timestamp: Date;
}

class AiChatApi {
  /**
   * Send a query to the AI chatbot
   */
  async sendQuery(request: ChatRequest): Promise<ChatResponse> {
    try {
      const { apiUrl } = getAppUrlInfo();
      const response = await apiClient<ChatResponse>({
        method: 'post',
        url: `${apiUrl}/ai-chat/query`,
        data: request,
      });
      return response.data;
    } catch (error: any) {
      console.error('Error sending AI chat query:', error);
      throw new Error(error.message || 'Failed to send query');
    }
  }

  /**
   * Send a streaming query to the AI chatbot using Server-Sent Events
   */
  async sendStreamingQuery(
    request: ChatRequest & { includeMeta?: boolean },
    onChunk: (chunk: StreamingChatResponse) => void,
    onComplete: (finalResponse: StreamingChatResponse) => void,
    onError: (error: Error) => void
  ): Promise<(() => void) | void> {
    try {
      const { apiUrl } = getAppUrlInfo();
      const params = new URLSearchParams({
        prompt: request.prompt,
        module: request.module || 'general',
        dateRange: request.dateRange ? JSON.stringify(request.dateRange) : '',
        categories: request.categories ? JSON.stringify(request.categories) : '',
      });
      if (request.includeMeta) params.set('includeMeta', 'true');

      const eventSource = new EventSource(`${apiUrl}/ai-chat/stream?${params.toString()}`);

      eventSource.onmessage = (event) => {
        try {
          const data: StreamingChatResponse = JSON.parse(event.data);
          onChunk(data);
          
          if (data.type === 'complete') {
            onComplete(data);
            eventSource.close();
          }
        } catch (parseError) {
          console.error('Error parsing streaming response:', parseError);
          onError(new Error('Failed to parse streaming response'));
        }
      };

      eventSource.onerror = (error) => {
        console.error('EventSource failed:', error);
        onError(new Error('Streaming connection failed'));
        eventSource.close();
      };

      // Return a cleanup function
      return () => {
        eventSource.close();
      };
    } catch (error: any) {
      console.error('Error setting up streaming query:', error);
      onError(new Error(error.message || 'Failed to setup streaming'));
    }
  }

  /**
   * Helper method to create a chat message
   */
  createMessage(role: 'user' | 'assistant', content: string, data?: any, metadata?: any): ChatMessage {
    return {
      id: `${Date.now()}-${Math.random()}`,
      role,
      content,
      data,
      metadata,
      timestamp: new Date(),
    };
  }
}

export default new AiChatApi();

